// ABOUTME: The cloud stack's geometry: four tiers (boxes and parts), eight bridge lobes and the canvas-to-stage mapping.
// ABOUTME: Every number is from the Cloud Stack Build Spec handoff, in stack-canvas pixels (a 360 x 512 box).
import type { CloudPalette } from './cloudPalette';

export type TierId = 'head' | 'upper' | 'lower' | 'base';
export type PaletteRole = keyof CloudPalette;

// Slab insets and bottom are px; the radius is the CSS shorthand. Height is a percent of the tier box.
export interface SlabPart {
  kind: 'slab';
  left: number;
  right: number;
  bottom: number;
  heightPct: number;
  radius: string;
}

// All four values are percents of the tier box.
export interface EllipsePart {
  kind: 'ellipse';
  leftPct: number;
  bottomPct: number;
  widthPct: number;
  heightPct: number;
}

export type TierPart = SlabPart | EllipsePart;

export interface Tier {
  id: TierId;
  box: readonly [x: number, y: number, w: number, h: number];
  z: number;
  parts: readonly TierPart[];
}

export interface Lobe {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  role: PaletteRole;
}

const slab = (left: number, right: number, bottom: number, heightPct: number, radius: string): SlabPart =>
  ({ kind: 'slab', left, right, bottom, heightPct, radius });
const ellipse = (leftPct: number, bottomPct: number, widthPct: number, heightPct: number): EllipsePart =>
  ({ kind: 'ellipse', leftPct, bottomPct, widthPct, heightPct });

// Listed back to front, so the order is also the paint order.
export const TIERS: readonly Tier[] = [
  {
    id: 'head', box: [34, 70, 300, 102], z: 2,
    parts: [
      slab(0, 0, 0, 52, '44px 44px 28px 28px'),
      ellipse(-2, 8, 22, 44), ellipse(82, 8, 20, 42), ellipse(8, 30, 28, 50),
      ellipse(32, 42, 36, 56), ellipse(64, 28, 28, 46), ellipse(12, -16, 24, 42),
    ],
  },
  {
    id: 'upper', box: [114, 109.5, 165, 138], z: 3,
    parts: [
      slab(0, 0, 0, 48, '44px 44px 26px 26px'),
      ellipse(-4, 8, 24, 40), ellipse(80, 10, 22, 38), ellipse(7, 2.5, 48, 62),
      ellipse(39, 4.5, 51, 70), ellipse(37, -14, 51, 70),
    ],
  },
  {
    id: 'lower', box: [63, 205, 215, 115], z: 4,
    parts: [
      slab(30, 20, -10, 48, '50px 50px 30px 30px'),
      ellipse(0.5, 8, 25, 44), ellipse(73.5, 6, 24, 46), ellipse(10, 24, 42, 64), ellipse(46, 24, 38, 54),
    ],
  },
  {
    id: 'base', box: [40, 276, 280, 96], z: 5,
    parts: [
      slab(27.5, 32.5, -2.5, 37.5, '48px 48px 30px 30px'),
      ellipse(-3.4, 4.3, 30.4, 52), ellipse(71, 6, 32.7, 37.8), ellipse(13.9, 31, 46.7, 52.9), ellipse(37, 25.8, 48.3, 51.8),
    ],
  },
];

// Painted under every tier, top to bottom. Roles are the Color Progression's B1 to B4R.
export const LOBES: readonly Lobe[] = [
  { id: 'L1', x: 0, y: 122, w: 88, h: 44, role: 'b4r' },
  { id: 'L2', x: 264, y: 118, w: 84, h: 42, role: 'b4r' },
  { id: 'L3', x: 57, y: 144, w: 90, h: 54, role: 'b4l' },
  { id: 'L4', x: 214, y: 153, w: 88, h: 46, role: 'b4l' },
  { id: 'L5', x: 84, y: 200, w: 58, h: 54, role: 'b3r' },
  { id: 'L6', x: 213, y: 211, w: 64, h: 60, role: 'b3l' },
  { id: 'L7', x: 60.5, y: 285, w: 66, h: 62, role: 'b2' },
  { id: 'L8', x: 224.5, y: 298.5, w: 70, h: 66, role: 'b1' },
];

// The stage the phone plate scales to fit. The stack root sits in it at x 9 and is 452 square.
export const STAGE = 470;
export const STACK_ROOT = { left: 9, size: 452, lift: 8, scale: 0.8186 } as const;
// The horizon sits at 58% of the stage; the stack stands on it.
export const STAGE_HORIZON = 0.58 * STAGE;
export const STACK_CANVAS = { width: 360, height: 512, rootLeft: 46, rootTop: -66 } as const;

// Canvas point to stage point: X = 235 + 0.8186 (x - 180), Y = 0.8186 (y - 66) - 8.
export function toStage(x: number, y: number): [number, number] {
  return [235 + STACK_ROOT.scale * (x - 180), STACK_ROOT.scale * (y - 66) - STACK_ROOT.lift];
}
